'use client';

import React, { useState } from 'react';
import { MapPin, Home, Calendar, ChevronLeft, ArrowRight, Clock } from 'lucide-react';
import type { BookingData } from './BookingWizard';

const branches = [
  { id: 'branch-noida-51', name: 'FIFTY ONE PLUS LAB — Sector 51', address: 'H-56, Sector 51, Noida 201301', distance: 'Official Branch' },
];

const getNextDates = () => {
  const dates = [];
  const today = new Date(2026, 6, 24); // Using static date for SSR safety
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    dates.push({
      id: `date-${i}`,
      label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dayNames[d.getDay()],
      date: `${String(d.getDate()).padStart(2, '0')} ${monthNames[d.getMonth()]}`,
      fullDate: `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/2026`,
      available: i !== 0 || true,
    });
  }
  return dates;
};

const morningSlots = ['7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM'];
const afternoonSlots = ['12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'];
const eveningSlots = ['5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM'];

const unavailableSlots = ['8:30 AM', '10:00 AM', '1:30 PM', '3:00 PM', '6:30 PM'];

export default function Step2BranchSlot({
  bookingData,
  updateBooking,
  onNext,
  onBack,
}: {
  bookingData: BookingData;
  updateBooking: (u: Partial<BookingData>) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const dates = getNextDates();

  const selectBranch = (id: string, name: string) => {
    updateBooking({ branch: id, branchName: name });
  };

  const selectDate = (date: string) => {
    updateBooking({ date, timeSlot: '' });
  };

  const selectSlot = (slot: string) => {
    if (!unavailableSlots.includes(slot)) {
      updateBooking({ timeSlot: slot });
    }
  };

  const canProceed =
    (bookingData.homeCollection || bookingData.branch) &&
    bookingData.date &&
    bookingData.timeSlot;

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="font-bold text-xl text-foreground">Branch & Appointment Slot</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Choose between visiting a branch or home sample collection, then pick your preferred date and time.
        </p>
      </div>

      <div className="p-6 space-y-7">
        {/* Home collection toggle */}
        <div className="bg-muted/50 border border-border rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bookingData.homeCollection ? 'bg-primary' : 'bg-secondary'}`}>
                <Home size={18} className={bookingData.homeCollection ? 'text-white' : 'text-primary'} />
              </div>
              <div>
                <div className="font-semibold text-sm text-foreground">Home Sample Collection</div>
                <div className="text-xs text-muted-foreground">Technician visits your location · ₹99 extra</div>
              </div>
            </div>
            <button
              onClick={() => updateBooking({ homeCollection: !bookingData.homeCollection, branch: '', branchName: '' })}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                bookingData.homeCollection ? 'bg-primary' : 'bg-border'
              }`}
              aria-label="Toggle home collection"
            >
              <span
                className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                  bookingData.homeCollection ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {bookingData.homeCollection && (
            <div className="mt-4">
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Collection Address
              </label>
              <input
                type="text"
                placeholder="Enter your full address, Flat/House No., Landmark"
                className="input-field text-sm"
                value={bookingData.homeAddress}
                onChange={(e) => updateBooking({ homeAddress: e.target.value })}
              />
            </div>
          )}
        </div>

        {/* Branch selection */}
        {!bookingData.homeCollection && (
          <div>
            <h3 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
              <MapPin size={15} className="text-primary" />
              Select Branch
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {branches.map((branch) => (
                <button
                  key={branch.id}
                  onClick={() => selectBranch(branch.id, branch.name)}
                  className={`text-left p-4 rounded-xl border-2 transition-all duration-150 ${
                    bookingData.branch === branch.id
                      ? 'border-primary bg-secondary' :'border-border bg-card hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-sm text-foreground mb-0.5">{branch.name}</div>
                      <div className="text-xs text-muted-foreground">{branch.address}</div>
                    </div>
                    <span className="text-xs text-primary font-semibold flex-shrink-0 bg-secondary px-2 py-0.5 rounded-full">
                      {branch.distance}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Date selection */}
        <div>
          <h3 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
            <Calendar size={15} className="text-primary" />
            Select Date
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {dates.map((d) => (
              <button
                key={d.id}
                onClick={() => selectDate(d.fullDate)}
                className={`flex-shrink-0 flex flex-col items-center px-4 py-3 rounded-xl border-2 transition-all duration-150 min-w-[72px] ${
                  bookingData.date === d.fullDate
                    ? 'border-primary bg-primary text-white' :'border-border bg-card hover:border-primary/50 text-foreground'
                }`}
              >
                <span className={`text-xs font-semibold ${bookingData.date === d.fullDate ? 'text-white/80' : 'text-muted-foreground'}`}>
                  {d.label}
                </span>
                <span className="text-sm font-bold mt-0.5">{d.date}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Time slots */}
        {bookingData.date && (
          <div>
            <h3 className="font-semibold text-sm text-foreground mb-3 flex items-center gap-2">
              <Clock size={15} className="text-primary" />
              Select Time Slot
            </h3>

            <div className="space-y-4">
              {/* Morning */}
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2">Morning</p>
                <div className="flex flex-wrap gap-2">
                  {morningSlots.map((slot) => (
                    <button
                      key={`morning-${slot}`}
                      onClick={() => selectSlot(slot)}
                      disabled={unavailableSlots.includes(slot)}
                      className={`slot-btn ${
                        bookingData.timeSlot === slot ? 'selected' : ''
                      } ${unavailableSlots.includes(slot) ? 'unavailable' : ''}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Afternoon */}
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2">Afternoon</p>
                <div className="flex flex-wrap gap-2">
                  {afternoonSlots.map((slot) => (
                    <button
                      key={`afternoon-${slot}`}
                      onClick={() => selectSlot(slot)}
                      disabled={unavailableSlots.includes(slot)}
                      className={`slot-btn ${
                        bookingData.timeSlot === slot ? 'selected' : ''
                      } ${unavailableSlots.includes(slot) ? 'unavailable' : ''}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Evening */}
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2">Evening</p>
                <div className="flex flex-wrap gap-2">
                  {eveningSlots.map((slot) => (
                    <button
                      key={`evening-${slot}`}
                      onClick={() => selectSlot(slot)}
                      disabled={unavailableSlots.includes(slot)}
                      className={`slot-btn ${
                        bookingData.timeSlot === slot ? 'selected' : ''
                      } ${unavailableSlots.includes(slot) ? 'unavailable' : ''}`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm border-2 border-primary bg-primary inline-block" />
                  Selected
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm border border-border bg-card inline-block" />
                  Available
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-muted inline-block opacity-50" />
                  Booked
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="pt-4 border-t border-border flex items-center justify-between">
          <button onClick={onBack} className="btn-outline py-2.5 px-5 text-sm flex items-center gap-2">
            <ChevronLeft size={15} />
            Back
          </button>
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="btn-primary"
          >
            Continue to Patient Details
            <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}